import { withFieldGroup } from '@massilia/features'
import { cn, Input, Label, RadioGroup, RadioGroupItem } from '@massilia/ui'

import { formatPrice } from '@/utils/format-price'

import type { CheckoutFormValues } from '../checkout-form.schema'

interface PaymentSectionProps {
  installmentAvailable: boolean
  minimumDeposit: number
  promoCodeDiscount: null | number
  totalPrice: number
}

type PaymentSectionValues = Pick<CheckoutFormValues, 'depositAmount' | 'isPayingInMultipleTimes'>

/**
 * Sous-choix « intégralité ou acompte » de l'option « Je paie maintenant ».
 * Rendu en lignes de radio nues : le panneau parent porte déjà la surface,
 * aucune carte supplémentaire ici.
 */
export const PaymentSection = withFieldGroup({
  defaultValues: {} as PaymentSectionValues,
  props: {} as PaymentSectionProps,
  render: function PaymentFields({
    group,
    installmentAvailable,
    minimumDeposit,
    promoCodeDiscount,
    totalPrice,
  }) {
    const effectiveTotal = Math.max(0, totalPrice - (promoCodeDiscount ?? 0))

    return (
      <group.Subscribe selector={(state) => state.values.isPayingInMultipleTimes}>
        {(isPayingInMultipleTimes) => (
          <RadioGroup
            className="flex w-full flex-col gap-0"
            onValueChange={(value) => {
              group.setFieldValue(
                'isPayingInMultipleTimes',
                value === 'is-paying-in-multiple-times'
              )
            }}
            value={
              isPayingInMultipleTimes
                ? 'is-paying-in-multiple-times'
                : 'is-not-paying-in-multiple-times'
            }
          >
            {/* Full payment */}
            <div className="flex items-center gap-2 py-2.5">
              <RadioGroupItem
                id="is-not-paying-in-multiple-times"
                value="is-not-paying-in-multiple-times"
              />
              <Label
                className="flex-1 cursor-pointer text-sm font-medium"
                htmlFor="is-not-paying-in-multiple-times"
              >
                L&apos;intégralité
              </Label>
              <span className="text-sm font-semibold text-gray-light-900">
                {formatPrice(effectiveTotal)}
              </span>
            </div>

            {/* Deposit */}
            {installmentAvailable && (
              <div className="flex flex-col border-t border-brand-100 py-2.5">
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    id="is-paying-in-multiple-times"
                    value="is-paying-in-multiple-times"
                  />
                  <Label
                    className="flex-1 cursor-pointer text-sm font-medium"
                    htmlFor="is-paying-in-multiple-times"
                  >
                    Je verse un acompte
                  </Label>
                </div>
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    isPayingInMultipleTimes
                      ? 'max-h-32 opacity-100'
                      : 'pointer-events-none max-h-0 opacity-0'
                  )}
                >
                  <group.AppField name="depositAmount">
                    {(field) => {
                      const isInvalid =
                        field.state.value !== undefined && field.state.value < minimumDeposit
                      return (
                        <div className="flex flex-col gap-1 pt-2 pl-6">
                          <div className="relative w-40">
                            <Input
                              aria-invalid={isInvalid}
                              aria-label="Montant de l'acompte"
                              className="[appearance:textfield] bg-white pr-8 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              inputMode="decimal"
                              min={minimumDeposit}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : Number(e.target.value)
                                field.handleChange(value)
                              }}
                              placeholder={String(minimumDeposit)}
                              type="number"
                              value={
                                !field.state.value || field.state.value === 0
                                  ? ''
                                  : field.state.value
                              }
                            />
                            <span
                              aria-hidden
                              className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-light-600"
                            >
                              €
                            </span>
                          </div>
                          <p
                            className={cn(
                              'text-xs font-medium',
                              isInvalid ? 'text-danger-foreground' : 'text-gray-light-600'
                            )}
                          >
                            Minimum {formatPrice(minimumDeposit)}
                          </p>
                        </div>
                      )
                    }}
                  </group.AppField>
                </div>
              </div>
            )}
          </RadioGroup>
        )}
      </group.Subscribe>
    )
  },
})
